<template>
  <div class="password-page page-container">
    <section class="page-header">
      <h1>密码生成器</h1>
      <p>使用 Argon2id 为任意主体派生强密码</p>
    </section>
    <el-alert v-if="!authBus.isLoggedIn" title="请先在页面右上角登录，派生参数历史会保存在你的账号下" type="warning" :closable="false" show-icon class="login-notice" />
    <div class="password-layout">
      <el-card shadow="never" class="generator-card">
        <div slot="header" class="card-title">密码生成器</div>
        <el-alert
          class="privacy-notice"
          title="隐私与安全声明"
          type="success"
          :closable="false"
          show-icon
        >
          <div slot="default">
            本站不会保存你的主密码和生成后的密码。主密码仅在本次请求中用于 Argon2id 派生，生成结果仅返回当前页面；历史记录只保存生成主体、密码版本号、长度和字符规则。
          </div>
        </el-alert>
        <el-form ref="form" label-position="top" @submit.native.prevent>
          <el-form-item label="主密码 *">
            <el-input ref="masterPassword" v-model="form.masterPassword" type="password" show-password maxlength="256" autocomplete="off" placeholder="仅用于本次派生，数据库不会保存" @input="clearResult" />
          </el-form-item>
          <el-form-item label="生成主体 *"><el-input v-model.trim="form.subject" maxlength="255" placeholder="网站域名 / 卡片 / 其他" @input="clearResult" /></el-form-item>
          <el-form-item label="密码版本号"><el-input-number v-model="form.passwordVersion" :min="1" :max="9999" controls-position="right" @change="clearResult" /></el-form-item>
          <el-divider content-position="left">高级参数</el-divider>
          <el-form-item :label="`密码长度：${form.passwordLength} 位`"><el-slider v-model="form.passwordLength" :min="8" :max="64" show-stops @change="clearResult" /></el-form-item>
          <el-form-item label="包含字符">
            <el-checkbox v-model="form.includeDigits" @change="clearResult">数字</el-checkbox>
            <el-checkbox v-model="form.includeUppercase" @change="clearResult">大写字母</el-checkbox>
            <el-checkbox v-model="form.includeLowercase" @change="clearResult">小写字母</el-checkbox>
            <el-checkbox v-model="form.includeSymbols" @change="clearResult">特殊字符</el-checkbox>
          </el-form-item>
          <el-form-item v-if="form.includeSymbols" label="允许的特殊字符">
            <el-input v-model="form.symbols" maxlength="32" show-word-limit @input="clearResult" />
          </el-form-item>
          <el-button type="primary" class="generate-button" :loading="generating" :disabled="!authBus.isLoggedIn" @click="generate">⚡ 立即生成密码</el-button>
        </el-form>
        <div v-if="generatedPassword" class="result-box">
          <span class="result-label">生成结果</span>
          <el-input :value="generatedPassword" :type="resultVisible ? 'text' : 'password'" readonly>
            <el-button slot="append" :icon="resultVisible ? 'el-icon-view' : 'el-icon-view'" @click="resultVisible = !resultVisible" />
          </el-input>
          <el-button type="success" icon="el-icon-document-copy" @click="copyPassword">复制</el-button>
        </div>
      </el-card>

      <el-card shadow="never" class="history-card">
        <div slot="header" class="card-title">历史使用记录</div>
        <el-input v-model.trim="keyword" prefix-icon="el-icon-search" clearable placeholder="搜索生成主体..." />
        <div v-if="historyLoading" v-loading="true" class="loading" />
        <el-empty v-else-if="!filteredHistory.length" description="暂无生成记录" />
        <div v-else class="history-list">
          <div v-for="item in filteredHistory" :key="item.id" class="history-item">
            <div class="history-title">{{ item.subject }}</div>
            <div class="history-meta">v{{ item.passwordVersion }} · {{ item.passwordLength }}位</div>
            <div class="history-rules">{{ ruleText(item) }}</div>
            <div class="history-actions">
              <el-button type="primary" size="mini" icon="el-icon-lightning" @click="useRecord(item)">一键填入并生成</el-button>
              <el-button type="danger" size="mini" plain icon="el-icon-delete" @click="removeRecord(item)" />
            </div>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { authBus } from '../store/auth'
import { deletePasswordHistory, generatePassword, listPasswordHistory } from '../api/passwordGenerator'

const DEFAULT_SYMBOLS = '!@#$%^&*()-_=+[]{};:,.?'

export default {
  name: 'PasswordGeneratorPage',
  data() {
    return {
      authBus, keyword: '', generating: false, historyLoading: false, history: [], generatedPassword: '', resultVisible: false,
      form: { masterPassword: '', subject: '', passwordVersion: 1, passwordLength: 16, includeDigits: true, includeUppercase: true, includeLowercase: true, includeSymbols: true, symbols: DEFAULT_SYMBOLS }
    }
  },
  computed: {
    filteredHistory() {
      const value = this.keyword.toLowerCase()
      return value ? this.history.filter(item => item.subject.toLowerCase().includes(value)) : this.history
    }
  },
  watch: {
    'authBus.isLoggedIn'(value) { if (value) this.loadHistory(); else this.history = [] }
  },
  mounted() { if (this.authBus.isLoggedIn) this.loadHistory() },
  beforeDestroy() { this.form.masterPassword = ''; this.generatedPassword = '' },
  methods: {
    clearResult() { this.generatedPassword = ''; this.resultVisible = false },
    validate() {
      if (this.form.masterPassword.length < 8) return '主密码至少需要8位'
      if (!this.form.subject) return '请输入生成主体'
      if (![this.form.includeDigits, this.form.includeUppercase, this.form.includeLowercase, this.form.includeSymbols].some(Boolean)) return '至少选择一种字符类型'
      if (this.form.includeSymbols && !this.form.symbols) return '请输入允许的特殊字符'
      return ''
    },
    async generate() {
      const error = this.validate()
      if (error) { this.$message.warning(error); return }
      this.generating = true
      try {
        const { data } = await generatePassword(this.form)
        if (data.code !== 200) throw new Error(data.message || '密码生成失败')
        this.generatedPassword = data.data.password
        this.resultVisible = false
        await this.loadHistory()
        this.$message.success('密码已生成，主密码和结果均未保存')
      } catch (error) {
        this.$message.error(error.response && error.response.data ? error.response.data.message : error.message)
      } finally { this.generating = false }
    },
    async loadHistory() {
      this.historyLoading = true
      try {
        const { data } = await listPasswordHistory()
        if (data.code === 200) this.history = data.data || []
      } catch (error) { this.history = [] } finally { this.historyLoading = false }
    },
    useRecord(item) {
      Object.assign(this.form, {
        subject: item.subject, passwordVersion: item.passwordVersion, passwordLength: item.passwordLength,
        includeDigits: item.includeDigits, includeUppercase: item.includeUppercase, includeLowercase: item.includeLowercase,
        includeSymbols: item.includeSymbols, symbols: item.symbols || DEFAULT_SYMBOLS
      })
      this.clearResult()
      if (this.form.masterPassword) this.generate()
      else { this.$message.info('参数已回填，请输入主密码后生成'); this.$nextTick(() => this.$refs.masterPassword.focus()) }
    },
    async removeRecord(item) {
      try {
        await this.$confirm(`确认删除 ${item.subject} 的记录吗？`, '删除记录', { type: 'warning' })
        const { data } = await deletePasswordHistory(item.id)
        if (data.code !== 200) throw new Error(data.message || '删除失败')
        this.history = this.history.filter(record => record.id !== item.id)
      } catch (error) { if (error !== 'cancel') this.$message.error(error.message || '删除失败') }
    },
    async copyPassword() {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.generatedPassword)
        } else {
          const input = document.createElement('textarea')
          input.value = this.generatedPassword
          input.style.position = 'fixed'
          input.style.opacity = '0'
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        this.$message.success('密码已复制')
      } catch (error) { this.$message.error('复制失败，请手动复制') }
    },
    ruleText(item) {
      const rules = []
      if (item.includeDigits) rules.push('数字')
      if (item.includeUppercase) rules.push('大写')
      if (item.includeLowercase) rules.push('小写')
      if (item.includeSymbols) rules.push('符号')
      return rules.join(' / ')
    }
  }
}
</script>

<style scoped>
.password-page { padding-top: 32px; padding-bottom: 80px; }
.login-notice { margin-top: 28px; }
.password-layout { display: grid; grid-template-columns: minmax(0, 3fr) minmax(300px, 2fr); gap: 24px; margin-top: 16px; }
.generator-card, .history-card { background: var(--surface); }
.card-title { color: var(--ink); font-size: 16px; font-weight: 600; }
.privacy-notice { margin-bottom: 20px; line-height: 1.7; }
.generate-button { width: 100%; }
.result-box { display: grid; grid-template-columns: auto 1fr auto; gap: 10px; align-items: center; padding-top: 20px; margin-top: 20px; border-top: 1px solid var(--line-soft); }
.result-label { font-weight: 600; }
.loading { min-height: 160px; }
.history-list { display: grid; gap: 12px; margin-top: 16px; }
.history-item { padding: 14px; background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-md); }
.history-title { color: var(--ink); font-size: 16px; font-weight: 600; }
.history-meta, .history-rules { margin-top: 5px; color: var(--ink-2); font-size: 13px; }
.history-rules { color: var(--ink-3); }
.history-actions { display: flex; justify-content: space-between; margin-top: 12px; }
@media (max-width: 900px) { .password-layout { grid-template-columns: 1fr; }.result-box { grid-template-columns: 1fr; } }
</style>
